// Ported from design-reference (oo-v2-skins/project/skins.jsx).
// A skin is a CSS-variable token bundle applied to <html> at boot.
// Keep this file in lockstep with:
//   tappya-dashboard-app/src/app/features/menu-links/data/skin-catalog.ts
//   marketing_website/src/lib/skins.ts

export type SkinTokens = Record<string, string>;

export interface Skin {
  id: string;
  name: string;
  audience: string;
  mode: "light" | "dark";
  tokens: SkinTokens;
}

export const SKINS: Skin[] = [
  {
    id: "verdant",
    name: "Verdant",
    audience: "Fast-casual healthy · bowls & wraps",
    mode: "light",
    tokens: {
      "--primary-50": "#e7f7ee",
      "--primary-100": "#c4ebd7",
      "--primary-600": "#09914a",
      "--primary-700": "#08793e",
      "--accent": "#0baa57",
      "--ink-1": "#1a201d",
      "--ink-2": "#5d6862",
      "--ink-3": "#828b87",
      "--surface-bg": "#f7f8f6",
      "--surface-1": "#ffffff",
      "--surface-2": "#f1f3f0",
      "--border": "#e5e8e4",
      "--border-strong": "#cfd3d1",
      "--radius-card": "16px",
      "--radius-btn": "12px",
      "--radius-chip": "999px",
      "--font-display": '"Plus Jakarta Sans", "Inter", system-ui, sans-serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Inter", system-ui, sans-serif',
      "--display-weight": "700",
      "--display-tracking": "-0.02em",
      "--display-case": "none",
      "--shadow-cta": "0 6px 14px -4px rgba(8,121,62,0.30)",
      "--cart-bg": "#1a201d",
      "--cart-fg": "#ffffff",
      "--hero-from": "#c4ebd7",
      "--hero-to": "#96dab7",
    },
  },
  {
    id: "ember",
    name: "Ember",
    audience: "QSR · smash burgers & fried chicken",
    mode: "light",
    tokens: {
      "--primary-50": "#fff1e3",
      "--primary-100": "#ffd6b0",
      "--primary-600": "#e94e1b",
      "--primary-700": "#b33810",
      "--accent": "#f2c14e",
      "--ink-1": "#1a1410",
      "--ink-2": "#5d5048",
      "--ink-3": "#8b7e74",
      "--surface-bg": "#fff8f0",
      "--surface-1": "#ffffff",
      "--surface-2": "#ffe9d6",
      "--border": "#f3dcc2",
      "--border-strong": "#e6c19b",
      "--radius-card": "22px",
      "--radius-btn": "999px",
      "--radius-chip": "999px",
      "--font-display": '"Archivo Black", "Inter", sans-serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Archivo", "Inter", sans-serif',
      "--display-weight": "900",
      "--display-tracking": "-0.03em",
      "--display-case": "uppercase",
      "--shadow-cta": "0 8px 0 -2px #b33810, 0 10px 22px -6px rgba(233,78,27,0.45)",
      "--cart-bg": "#1a1410",
      "--cart-fg": "#fff8f0",
      "--hero-from": "#ffd6b0",
      "--hero-to": "#e94e1b",
    },
  },
  {
    id: "atelier",
    name: "Atelier",
    audience: "Fine dining · tasting menus & wine",
    mode: "light",
    tokens: {
      "--primary-50": "#f3efe6",
      "--primary-100": "#e6dfcc",
      "--primary-600": "#1a1a1a",
      "--primary-700": "#0a0a0a",
      "--accent": "#a98a4b",
      "--ink-1": "#0a0a0a",
      "--ink-2": "#4d4d4d",
      "--ink-3": "#8a8a8a",
      "--surface-bg": "#faf7f1",
      "--surface-1": "#ffffff",
      "--surface-2": "#f3efe6",
      "--border": "#e8e2d3",
      "--border-strong": "#c9c0a8",
      "--radius-card": "2px",
      "--radius-btn": "2px",
      "--radius-chip": "2px",
      "--font-display": '"Cormorant Garamond", "Times New Roman", serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Cormorant Garamond", serif',
      "--display-weight": "500",
      "--display-tracking": "-0.01em",
      "--display-case": "none",
      "--shadow-cta": "none",
      "--cart-bg": "#0a0a0a",
      "--cart-fg": "#faf7f1",
      "--hero-from": "#f3efe6",
      "--hero-to": "#e6dfcc",
    },
  },
  {
    id: "hearth",
    name: "Hearth",
    audience: "Specialty coffee & bakery",
    mode: "light",
    tokens: {
      "--primary-50": "#f3e9d8",
      "--primary-100": "#e5d1a9",
      "--primary-600": "#7a4a2b",
      "--primary-700": "#5a3520",
      "--accent": "#c98a4a",
      "--ink-1": "#2a1d13",
      "--ink-2": "#6b5346",
      "--ink-3": "#998a7c",
      "--surface-bg": "#f8f1e6",
      "--surface-1": "#fffaf2",
      "--surface-2": "#f0e5d4",
      "--border": "#e6d8be",
      "--border-strong": "#c9b58e",
      "--radius-card": "14px",
      "--radius-btn": "10px",
      "--radius-chip": "999px",
      "--font-display": '"DM Serif Display", "Times New Roman", serif',
      "--font-ui": '"Work Sans", "Inter", sans-serif',
      "--font-num": '"Work Sans", sans-serif',
      "--display-weight": "400",
      "--display-tracking": "-0.015em",
      "--display-case": "none",
      "--shadow-cta": "0 4px 12px -4px rgba(122,74,43,0.30)",
      "--cart-bg": "#2a1d13",
      "--cart-fg": "#fffaf2",
      "--hero-from": "#e5d1a9",
      "--hero-to": "#c98a4a",
    },
  },
  {
    id: "boba",
    name: "Boba",
    audience: "Bubble tea · Gen Z dessert spots",
    mode: "light",
    tokens: {
      "--primary-50": "#fbeaff",
      "--primary-100": "#f3d3ff",
      "--primary-600": "#b76acc",
      "--primary-700": "#8e3fa6",
      "--accent": "#ff9ec5",
      "--ink-1": "#2a1f2e",
      "--ink-2": "#5e4b66",
      "--ink-3": "#9788a0",
      "--surface-bg": "#faf3ff",
      "--surface-1": "#ffffff",
      "--surface-2": "#f3e3fa",
      "--border": "#ecd9f5",
      "--border-strong": "#d6b3e6",
      "--radius-card": "26px",
      "--radius-btn": "999px",
      "--radius-chip": "999px",
      "--font-display": '"Nunito", "Inter", sans-serif',
      "--font-ui": '"Nunito", "Inter", sans-serif',
      "--font-num": '"Nunito", sans-serif',
      "--display-weight": "800",
      "--display-tracking": "-0.025em",
      "--display-case": "none",
      "--shadow-cta": "0 10px 24px -6px rgba(183,106,204,0.50)",
      "--cart-bg": "#2a1f2e",
      "--cart-fg": "#ffffff",
      "--hero-from": "#f3d3ff",
      "--hero-to": "#ff9ec5",
    },
  },
  {
    id: "forno",
    name: "Forno",
    audience: "Trattoria · pizza & pasta",
    mode: "light",
    tokens: {
      "--primary-50": "#f7ece1",
      "--primary-100": "#ecd2b3",
      "--primary-600": "#a8230e",
      "--primary-700": "#7a1709",
      "--accent": "#5d6a2f",
      "--ink-1": "#1f1410",
      "--ink-2": "#5a4b41",
      "--ink-3": "#9a8a7e",
      "--surface-bg": "#f6efe2",
      "--surface-1": "#fffbf2",
      "--surface-2": "#ede2cd",
      "--border": "#e0d2b7",
      "--border-strong": "#bca884",
      "--radius-card": "4px",
      "--radius-btn": "6px",
      "--radius-chip": "4px",
      "--font-display": '"Bodoni Moda", "Times New Roman", serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Bodoni Moda", serif',
      "--display-weight": "700",
      "--display-tracking": "-0.015em",
      "--display-case": "none",
      "--shadow-cta": "0 4px 0 -1px #7a1709",
      "--cart-bg": "#1f1410",
      "--cart-fg": "#f6efe2",
      "--hero-from": "#ecd2b3",
      "--hero-to": "#a8230e",
    },
  },
  {
    id: "kaiseki",
    name: "Kaiseki",
    audience: "Japanese · sushi & izakaya",
    mode: "light",
    tokens: {
      "--primary-50": "#f7ece9",
      "--primary-100": "#f0d2cb",
      "--primary-600": "#c9352c",
      "--primary-700": "#982016",
      "--accent": "#0a0a0a",
      "--ink-1": "#0a0a0a",
      "--ink-2": "#4a4a4a",
      "--ink-3": "#888781",
      "--surface-bg": "#f7f4ec",
      "--surface-1": "#ffffff",
      "--surface-2": "#ebe6db",
      "--border": "#dcd5c5",
      "--border-strong": "#b8b09c",
      "--radius-card": "0px",
      "--radius-btn": "0px",
      "--radius-chip": "0px",
      "--font-display": '"Shippori Mincho", "Noto Serif JP", "Times New Roman", serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Shippori Mincho", serif',
      "--display-weight": "600",
      "--display-tracking": "0.02em",
      "--display-case": "none",
      "--shadow-cta": "none",
      "--cart-bg": "#0a0a0a",
      "--cart-fg": "#f7f4ec",
      "--hero-from": "#f0d2cb",
      "--hero-to": "#f7ece9",
    },
  },
  {
    id: "midnight",
    name: "Midnight",
    audience: "Late-night bar · cocktails & small plates",
    mode: "dark",
    tokens: {
      "--primary-50": "#2a2316",
      "--primary-100": "#3d3520",
      "--primary-600": "#ffd75c",
      "--primary-700": "#f0c43c",
      "--accent": "#5ce1e6",
      "--ink-1": "#f5f0e1",
      "--ink-2": "#b3ada0",
      "--ink-3": "#6d6a64",
      "--surface-bg": "#0e0f12",
      "--surface-1": "#16181d",
      "--surface-2": "#1d2026",
      "--border": "#262a32",
      "--border-strong": "#363a44",
      "--radius-card": "10px",
      "--radius-btn": "8px",
      "--radius-chip": "999px",
      "--font-display": '"Space Grotesk", "Inter", sans-serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Space Grotesk", sans-serif',
      "--display-weight": "600",
      "--display-tracking": "-0.025em",
      "--display-case": "none",
      "--shadow-cta":
        "0 0 0 1px rgba(255,215,92,0.30), 0 8px 24px -4px rgba(255,215,92,0.35)",
      "--cart-bg": "#ffd75c",
      "--cart-fg": "#0e0f12",
      "--hero-from": "#1d2026",
      "--hero-to": "#2a2316",
    },
  },
  {
    id: "salsa",
    name: "Salsa",
    audience: "Taqueria · Mexican street food",
    mode: "light",
    tokens: {
      "--primary-50": "#fff0d6",
      "--primary-100": "#ffd58c",
      "--primary-600": "#d94312",
      "--primary-700": "#a82f08",
      "--accent": "#3b8a3a",
      "--ink-1": "#1f1208",
      "--ink-2": "#5a4030",
      "--ink-3": "#8e7460",
      "--surface-bg": "#fef5e3",
      "--surface-1": "#fffaf0",
      "--surface-2": "#fbe6c2",
      "--border": "#f0d8a8",
      "--border-strong": "#d9b478",
      "--radius-card": "8px",
      "--radius-btn": "8px",
      "--radius-chip": "999px",
      "--font-display": '"Alfa Slab One", "Archivo Black", serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Archivo", "Inter", sans-serif',
      "--display-weight": "400",
      "--display-tracking": "-0.005em",
      "--display-case": "none",
      "--shadow-cta": "0 6px 0 -1px #a82f08, 0 10px 18px -6px rgba(217,67,18,0.45)",
      "--cart-bg": "#1f1208",
      "--cart-fg": "#fef5e3",
      "--hero-from": "#ffd58c",
      "--hero-to": "#d94312",
    },
  },
  {
    id: "masala",
    name: "Masala",
    audience: "Indian · curry house & thali",
    mode: "light",
    tokens: {
      "--primary-50": "#fff4d6",
      "--primary-100": "#fadf9a",
      "--primary-600": "#b8431c",
      "--primary-700": "#7e2a0e",
      "--accent": "#d49a16",
      "--ink-1": "#1d130a",
      "--ink-2": "#5a4434",
      "--ink-3": "#8e7460",
      "--surface-bg": "#fbf0d6",
      "--surface-1": "#fff8e8",
      "--surface-2": "#f5e0b4",
      "--border": "#ead193",
      "--border-strong": "#caa760",
      "--radius-card": "12px",
      "--radius-btn": "10px",
      "--radius-chip": "999px",
      "--font-display": '"Fraunces", "Cormorant Garamond", serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Fraunces", serif',
      "--display-weight": "700",
      "--display-tracking": "-0.02em",
      "--display-case": "none",
      "--shadow-cta": "0 6px 14px -4px rgba(184,67,28,0.45)",
      "--cart-bg": "#1d130a",
      "--cart-fg": "#fbf0d6",
      "--hero-from": "#fadf9a",
      "--hero-to": "#d49a16",
    },
  },
  {
    id: "aegean",
    name: "Aegean",
    audience: "Mediterranean · Greek tavern",
    mode: "light",
    tokens: {
      "--primary-50": "#e6f1f8",
      "--primary-100": "#bcdcef",
      "--primary-600": "#1a6cb0",
      "--primary-700": "#0e4f86",
      "--accent": "#e8bf3a",
      "--ink-1": "#0e2233",
      "--ink-2": "#4e6577",
      "--ink-3": "#8499a8",
      "--surface-bg": "#f7faf9",
      "--surface-1": "#ffffff",
      "--surface-2": "#ecf2f3",
      "--border": "#dde6e7",
      "--border-strong": "#c0cdcf",
      "--radius-card": "18px",
      "--radius-btn": "12px",
      "--radius-chip": "999px",
      "--font-display": '"Playfair Display", "Cormorant Garamond", serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Playfair Display", serif',
      "--display-weight": "700",
      "--display-tracking": "-0.02em",
      "--display-case": "none",
      "--shadow-cta": "0 6px 16px -4px rgba(26,108,176,0.40)",
      "--cart-bg": "#0e2233",
      "--cart-fg": "#f7faf9",
      "--hero-from": "#bcdcef",
      "--hero-to": "#1a6cb0",
    },
  },
  {
    id: "diner",
    name: "Diner",
    audience: "American diner · retro all-day",
    mode: "light",
    tokens: {
      "--primary-50": "#fbe6e4",
      "--primary-100": "#f4b8b3",
      "--primary-600": "#c8232c",
      "--primary-700": "#921219",
      "--accent": "#0d8a8a",
      "--ink-1": "#1a1a1a",
      "--ink-2": "#4a4a4a",
      "--ink-3": "#888888",
      "--surface-bg": "#fdf6e8",
      "--surface-1": "#fffdf5",
      "--surface-2": "#f3e8d0",
      "--border": "#e6d8b5",
      "--border-strong": "#c9b58a",
      "--radius-card": "6px",
      "--radius-btn": "4px",
      "--radius-chip": "4px",
      "--font-display": '"Bungee", "Archivo Black", sans-serif',
      "--font-ui": '"Work Sans", "Inter", sans-serif',
      "--font-num": '"Bungee", monospace',
      "--display-weight": "400",
      "--display-tracking": "0.01em",
      "--display-case": "uppercase",
      "--shadow-cta": "0 5px 0 -1px #921219, 0 9px 16px -6px rgba(200,35,44,0.50)",
      "--cart-bg": "#1a1a1a",
      "--cart-fg": "#fffdf5",
      "--hero-from": "#f4b8b3",
      "--hero-to": "#c8232c",
    },
  },
  {
    id: "smoke",
    name: "Smoke",
    audience: "BBQ · smokehouse & sides",
    mode: "dark",
    tokens: {
      "--primary-50": "#3a2418",
      "--primary-100": "#5a3520",
      "--primary-600": "#e87a2c",
      "--primary-700": "#f0a04a",
      "--accent": "#e0c068",
      "--ink-1": "#f4ebde",
      "--ink-2": "#b8a999",
      "--ink-3": "#7a6a5e",
      "--surface-bg": "#1a120c",
      "--surface-1": "#2a1f17",
      "--surface-2": "#352820",
      "--border": "#3e2f25",
      "--border-strong": "#564238",
      "--radius-card": "6px",
      "--radius-btn": "6px",
      "--radius-chip": "4px",
      "--font-display": '"Oswald", "Archivo Black", sans-serif',
      "--font-ui": '"Inter", system-ui, sans-serif',
      "--font-num": '"Oswald", sans-serif',
      "--display-weight": "700",
      "--display-tracking": "0.005em",
      "--display-case": "uppercase",
      "--shadow-cta": "0 5px 0 -1px #8a3408, 0 10px 22px -6px rgba(232,122,44,0.50)",
      "--cart-bg": "#e87a2c",
      "--cart-fg": "#1a120c",
      "--hero-from": "#5a3520",
      "--hero-to": "#e87a2c",
    },
  },
  {
    id: "pulse",
    name: "Pulse",
    audience: "Juice bar · smoothies & açaí",
    mode: "light",
    tokens: {
      "--primary-50": "#e7fadf",
      "--primary-100": "#c0f0b1",
      "--primary-600": "#22c45e",
      "--primary-700": "#0f8a3f",
      "--accent": "#ff5d8f",
      "--ink-1": "#0a1f14",
      "--ink-2": "#3f5147",
      "--ink-3": "#7a8d83",
      "--surface-bg": "#f4fbf2",
      "--surface-1": "#ffffff",
      "--surface-2": "#e5f3e0",
      "--border": "#d4e8cd",
      "--border-strong": "#a8d098",
      "--radius-card": "20px",
      "--radius-btn": "999px",
      "--radius-chip": "999px",
      "--font-display": '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
      "--font-ui": '"Plus Jakarta Sans", "Inter", sans-serif',
      "--font-num": '"Bricolage Grotesque", sans-serif',
      "--display-weight": "800",
      "--display-tracking": "-0.035em",
      "--display-case": "none",
      "--shadow-cta": "0 10px 24px -6px rgba(34,196,94,0.45)",
      "--cart-bg": "#0a1f14",
      "--cart-fg": "#e7fadf",
      "--hero-from": "#c0f0b1",
      "--hero-to": "#ff5d8f",
    },
  },
];

export function getSkin(id: string | null | undefined): Skin | null {
  if (!id) return null;
  return SKINS.find((s) => s.id === id) ?? null;
}
